import React, { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Save, Trash2, RefreshCw, KeyRound, X, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;  // 10 rows per page keeps the window load fast (Iter42b)
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } };
const yesNo = (v) => v ? "✓" : "—";

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "bg-[#F4F4F0] text-[#111]",
    orange:  "bg-[#FF4F00] text-white",
    green:   "bg-[#00A36C] text-white",
    dim:     "bg-[#EEE] text-[#666]",
    red:     "bg-[#B00020] text-white",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono-cal uppercase tracking-widest ${tones[tone] || tones.default}`}>{children}</span>;
}

function EditModal({ user, orgs, currentAdminId, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: user.name || "",
    is_authorized: !!user.is_authorized,
    is_org_admin: !!user.is_org_admin,
    is_platform_owner: !!user.is_platform_owner,
    email_verified: !!user.email_verified,
    org_id: user.org_id || "",
  }));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const isSelf = user.id === currentAdminId;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${user.id}`, form);
      toast.success("User updated");
      onSaved(r.data.user || user);
    } catch (e) { toast.error(apiError(e, "Update failed")); }
    finally { setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success("User deleted");
      onSaved(null);
    } catch (e) { toast.error(apiError(e, "Delete failed")); setConfirmDelete(false); }
    finally { setSaving(false); }
  };

  const sendReset = async () => {
    setSendingReset(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/reset-password`);
      toast[r.data.email_sent ? "success" : "warning"](r.data.email_sent ? "Reset email sent" : "Email backend not configured — token created only");
    } catch (e) { toast.error(apiError(e, "Reset failed")); }
    finally { setSendingReset(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-6 overflow-auto" onClick={onClose} data-testid="edit-user-modal">
      <div className="cal-card max-w-2xl w-full p-8 bg-white mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ EDIT USER ]</div>
            <h2 className="font-display font-bold text-2xl mt-1">{user.email}</h2>
            <div className="text-sm text-[#666] mt-1">ID: <span className="font-mono-cal">{user.id}</span></div>
            {isSelf && <div className="mt-2"><Pill tone="orange">THIS IS YOU</Pill></div>}
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-[#111]" data-testid="edit-close"><X size={20} /></button>
        </div>

        <div className="space-y-5">
          <div>
            <label className="cal-label">Display name</label>
            <input className="cal-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" />
          </div>

          <div>
            <label className="cal-label">Organization</label>
            <select className="cal-input" value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} data-testid="edit-org">
              <option value="">(unassigned)</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              ["is_authorized",     "Authorized (can use the app)"],
              ["is_org_admin",      "Org Admin"],
              ["is_platform_owner", "Platform Owner (god mode)"],
              ["email_verified",    "Email verified"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 p-3 border border-[#111]/20 cursor-pointer hover:border-[#FF4F00]">
                <input
                  type="checkbox"
                  checked={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                  data-testid={`edit-${k.replace(/_/g, '-')}`}
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>

          <div className="pt-4 border-t border-[#EEE] flex flex-wrap gap-3 items-center">
            <button onClick={save} disabled={saving} className="cal-btn" data-testid="edit-save">
              <Save size={14} className="inline-block mr-1" /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button onClick={sendReset} disabled={sendingReset} className="cal-btn cal-btn-ghost" data-testid="edit-send-reset">
              <KeyRound size={14} className="inline-block mr-1" /> {sendingReset ? "Sending…" : "Send password reset"}
            </button>
            <div className="flex-1" />
            {isSelf ? (
              <span className="text-xs text-[#666] font-mono-cal uppercase">[SELF-DELETE BLOCKED]</span>
            ) : confirmDelete ? (
              <>
                <span className="text-xs text-[#B00020] font-mono-cal uppercase">CONFIRM?</span>
                <button onClick={del} disabled={saving} className="cal-btn" style={{ background: "#B00020", borderColor: "#B00020" }} data-testid="edit-confirm-delete">
                  Delete permanently
                </button>
                <button onClick={() => setConfirmDelete(false)} className="cal-btn cal-btn-ghost">Cancel</button>
              </>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="cal-btn cal-btn-ghost" data-testid="edit-delete-btn">
                <Trash2 size={14} className="inline-block mr-1" /> Delete user
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlatformUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (q.trim()) params.q = q.trim();
      if (orgFilter) params.org_id = orgFilter;
      // Fetch the org list only on the first load (using a functional set
      // avoids reading the outer `orgs` state and closing over stale values).
      const [uRes, oRes] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/orgs").catch(() => ({ data: null })),
      ]);
      setUsers(uRes.data.users || []);
      setTotal(uRes.data.total || 0);
      if (oRes.data) setOrgs((prev) => (prev.length === 0 ? oRes.data : prev));
    } catch (e) { toast.error(apiError(e, "Failed to load users")); }
    finally { setLoading(false); }
  }, [q, orgFilter, page]);

  useEffect(() => { load(); }, [load]);

  if (me && !me.is_platform_owner) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="platform-users-forbidden">
        <div className="font-mono-cal text-xs uppercase tracking-widest text-[#666] mb-2">[ 403 ]</div>
        <h1 className="font-display font-bold text-3xl mb-3">Platform Owner only</h1>
        <p className="text-[#666]">This page is restricted to Platform Owner accounts.</p>
      </div>
    );
  }

  const onSaved = (updated) => {
    if (updated === null) {
      // Deleted
      setUsers((prev) => prev.filter((u) => u.id !== editing.id));
      setTotal((t) => Math.max(0, t - 1));
    } else {
      // Preserve the org_name lookup that admin_list_users injects
      setUsers((prev) => prev.map((u) => u.id === updated.id ? { ...u, ...updated, org_name: orgs.find((o) => o.id === updated.org_id)?.name || "" } : u));
    }
    setEditing(null);
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="platform-users-page">
      <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00]">[ PLATFORM OWNER · GOD MODE ]</div>
          <h1 className="font-display font-bold text-4xl tracking-tight mt-1">All users</h1>
          <p className="text-[#666] mt-2 text-sm">Manage every user across every organization. {total} total.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/admin/platform" className="cal-btn cal-btn-ghost" data-testid="users-back-platform">← Plans &amp; Orgs</Link>
          <button onClick={load} className="cal-btn cal-btn-ghost" data-testid="users-refresh"><RefreshCw size={14} className="inline-block mr-1" /> Refresh</button>
        </div>
      </div>

      <div className="cal-card p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="cal-label"><Search size={12} className="inline mr-1" /> Search email or name</label>
          <input className="cal-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="e.g. jane@acme" data-testid="users-search" />
        </div>
        <div className="min-w-[220px]">
          <label className="cal-label">Filter by org</label>
          <select className="cal-input" value={orgFilter} onChange={(e) => { setOrgFilter(e.target.value); setPage(0); }} data-testid="users-org-filter">
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.domain})</option>)}
          </select>
        </div>
      </div>

      <div className="cal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#111] text-white font-mono-cal text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Organization</th>
              <th className="text-center px-3 py-3">Auth</th>
              <th className="text-center px-3 py-3">Admin</th>
              <th className="text-center px-3 py-3">Owner</th>
              <th className="text-center px-3 py-3">Verified</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[#666]">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[#666]">No users match your filters.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-[#EEE] hover:bg-[#FAFAF7]" data-testid={`users-row-${u.id}`}>
                <td className="px-4 py-3 font-mono-cal text-xs break-all">
                  {u.email}
                  {u.is_platform_owner && <span className="ml-2"><Pill tone="orange"><ShieldCheck size={9} className="inline" /> OWNER</Pill></span>}
                  {u.id === me?.id && <span className="ml-2"><Pill tone="dim">YOU</Pill></span>}
                </td>
                <td className="px-4 py-3">{u.name || <span className="text-[#999]">—</span>}</td>
                <td className="px-4 py-3 text-xs">{u.org_name || <span className="text-[#B00020] font-mono-cal">ORPHAN</span>}</td>
                <td className="px-3 py-3 text-center">{yesNo(u.is_authorized)}</td>
                <td className="px-3 py-3 text-center">{yesNo(u.is_org_admin)}</td>
                <td className="px-3 py-3 text-center">{yesNo(u.is_platform_owner)}</td>
                <td className="px-3 py-3 text-center">{yesNo(u.email_verified)}</td>
                <td className="px-4 py-3 text-xs text-[#666]">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(u)} className="cal-btn cal-btn-ghost" data-testid={`users-edit-${u.id}`}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-6 font-mono-cal text-xs text-[#666]" data-testid="users-pagination">
          <div>Page {page + 1} of {pages} · {total} users total</div>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="cal-btn cal-btn-ghost disabled:opacity-40" data-testid="users-prev">← Prev</button>
            <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="cal-btn cal-btn-ghost disabled:opacity-40" data-testid="users-next">Next →</button>
          </div>
        </div>
      )}

      {editing && (
        <EditModal user={editing} orgs={orgs} currentAdminId={me?.id} onClose={() => setEditing(null)} onSaved={onSaved} />
      )}
    </div>
  );
}
